import React, { useState } from "react";
import { Row, Col } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import jsPDF from "jspdf";

function get(obj, path, fallback) {
  var result = obj;
  var parts = path.split(".");
  for (var i = 0; i < parts.length; i++) {
    if (result === null || result === undefined) return fallback;
    result = result[parts[i]];
  }
  return result === undefined || result === null ? fallback : result;
}

function normalizeImages(val) {
  if (!val) return [];
  if (Array.isArray(val)) {
    return val
      .map((item) => {
        if (typeof item === "string") return item;
        if (typeof item === "object") {
          return item.media_location || item.link || item.url || null;
        }
        return null;
      })
      .filter(Boolean);
  }
  return [];
}

const loadImage = (imageUrl) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = function() {
      resolve({ element: img, width: img.width, height: img.height, loaded: true });
    };
    
    img.onerror = function() {
      resolve({ element: null, width: 0, height: 0, loaded: false });
    };
    
    img.src = imageUrl + (imageUrl.includes('?') ? '&' : '?') + 't=' + Date.now();
  });
};

function renderValue(val, isBulletField = false) {
  if (val === null || val === undefined || val === "") return <span className="text-muted">-</span>;

  if (typeof val === "string") {
    return val.replace(/<\/?p>/gi, " ").trim();
  }

  if (Array.isArray(val)) {
    if (val.length === 0) return <span className="text-muted">-</span>;
    
    if (isBulletField) {
      return (
        <ul style={{ paddingLeft: '20px', margin: 0 }}>
          {val.map((item, i) => (
            <li key={i} style={{ marginBottom: '4px' }}>
              {typeof item === "string" ? item.replace(/<\/?p>/gi, " ").trim() : renderValue(item, isBulletField)}
            </li>
          ))}
        </ul>
      );
    } else {
      if (typeof val[0] === "object" && val[0] !== null) {
        return val.map((obj, i) => (
          <div key={i} className="mb-1" style={{ paddingLeft: "10px", borderLeft: "2px solid #e9ecef" }}>
            {Object.entries(obj).map(([k, v]) => (
              <div key={k}>
                <b>{k}:</b> {renderValue(v, isBulletField)}
              </div>
            ))}
          </div>
        ));
      }
      return val.map(v => typeof v === "string" ? v.replace(/<\/?p>/gi, " ").trim() : v).join(", ");
    }
  }

  if (typeof val === "object" && val !== null) {
    return (
      <div style={{ paddingLeft: "10px", borderLeft: "2px solid #e9ecef" }}>
        {Object.entries(val).map(([k, v]) => (
          <div key={k}>
            <b>{k}:</b> {renderValue(v, isBulletField)}
          </div>
        ))}
      </div>
    );
  }

  return String(val);
}

function renderUpdatedValueArray(oldVal, newVal, isBulletField = false) {
  const oldArr = Array.isArray(oldVal) ? oldVal : [];
  
  if (isBulletField) {
    return (
      <ul style={{ paddingLeft: '20px', margin: 0 }}>
        {newVal.map((item, i) => {
          const isItemChanged = JSON.stringify(item) !== JSON.stringify(oldArr[i]);
          return (
            <li key={i} style={{
              backgroundColor: isItemChanged ? '#A0FAFA' : 'transparent',
              padding: isItemChanged ? '2px 4px' : '0',
              borderRadius: '3px',
              marginBottom: '4px',
              display: 'list-item'
            }}>
              {typeof item === "string" ? item.replace(/<\/?p>/gi, " ").trim() : renderValue(item, isBulletField)}
            </li>
          )
        })}
      </ul>
    );
  } else {
    return (
      <div style={{ display: 'inline' }}>
        {newVal.map((item, i) => {
          const isItemChanged = JSON.stringify(item) !== JSON.stringify(oldArr[i]);
          return (
            <span key={i}>
              <span style={{
                backgroundColor: isItemChanged ? '#A0FAFA' : 'transparent',
                padding: isItemChanged ? '2px 4px' : '0',
                borderRadius: '3px',
              }}>
                {typeof item === "string" ? item.replace(/<\/?p>/gi, " ").trim() : renderValue(item, isBulletField)}
              </span>
              {i < newVal.length - 1 ? ", " : ""}
            </span>
          )
        })}
      </div>
    );
  }
}

function renderImagesList(val) {
  const imgs = normalizeImages(val);
  if (imgs.length === 0) return <span className="text-muted">-</span>;
  return (
    <div className="d-flex flex-wrap">
      {imgs.map((img, i) => (
        <img key={i} src={img} alt="preview" className="preview-image-item" />
      ))}
    </div>
  );
}

function getStringValue(val, isBulletField = false, depth = 0) {
  if (val === null || val === undefined || val === "") return "-";
  
  const indent = "  ".repeat(depth);

  if (typeof val === "string") {
    return val.replace(/<\/?p>/gi, " ").trim();
  }

  if (Array.isArray(val)) {
    if (val.length === 0) return "-";
    if (isBulletField) {
      return val.map(item => `• ${getStringValue(item, isBulletField, depth)}`).join("\n");
    } else {
      return val.map(item => getStringValue(item, isBulletField, depth)).join(", ");
    }
  }

  if (typeof val === "object" && val !== null) {
    return Object.entries(val).map(([k, v]) => {
      if (typeof v === "object" && v !== null && Object.keys(v).length > 0) {
        return `${indent}${k}:\n${getStringValue(v, isBulletField, depth + 1)}`;
      } else {
        return `${indent}${k}: ${getStringValue(v, isBulletField, 0)}`;
      }
    }).join("\n");
  }

  return String(val);
}

function wrapText(doc, text, maxWidth) {
  if (!text) return ["-"];
  const paragraphs = text.split('\n');
  let lines = [];
  paragraphs.forEach(p => {
    const pLines = doc.splitTextToSize(p, maxWidth);
    lines = lines.concat(pLines);
  });
  return lines;
}

function AmazonProductListingPreview({ 
  groupedFields, 
  changedFields = {}, 
  storeName = "Store", 
  asin = "ASIN", 
  sku = "SKU" 
}) {
  const [isDownloading, setIsDownloading] = useState(false);

  const TAB_ORDER = [
    "Product Details",
    "Product Identity",
    "Variations",
    "Offer",
    "Images",
    "Shipping",
    "Safety & Compliance"
  ];

  const handleDownloadPdf = async () => {
    setIsDownloading(true);
    try {
      const doc = new jsPDF();
      
      const cleanStoreName = storeName.replace(/\s+/g, '_');
      const cleanAsin = asin !== "\u2014" ? asin : "ASIN";
      const cleanSku = sku !== "\u2014" ? sku : "SKU";

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const leftMargin = 15;
      const rightMargin = 15;
      const contentWidth = pageWidth - leftMargin - rightMargin;
      let yPos = 20;

      const checkPageBreak = (requiredSpace) => {
        if (yPos + requiredSpace > pageHeight - 15) {
          doc.addPage();
          yPos = 20;
          return true;
        }
        return false;
      };

      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      const pdfTitle = `Listing Optimizer - ${cleanStoreName}_${cleanAsin}_${cleanSku}`;
      const splitTitle = wrapText(doc, pdfTitle, contentWidth);
      doc.text(splitTitle, leftMargin, yPos);
      yPos += (splitTitle.length * 6) + 10;

      const sortedGroups = Object.keys(groupedFields).sort((a, b) => {
        const getIndex = (name) => {
          const foundIndex = TAB_ORDER.findIndex(o => name.toLowerCase() === o.toLowerCase());
          return foundIndex !== -1 ? foundIndex : 999;
        };
        return getIndex(a) - getIndex(b);
      });

      const imageUrls = new Set();
      sortedGroups.forEach((group) => {
        const fields = groupedFields[group];
        if (!fields) return;
        fields.forEach((f) => {
          const isImageField = f.field === "images" || (f.field && f.field.includes("image_locator")) || (f.ui && f.ui.input_type === "image");
          if (isImageField) {
            const oldVal = get(f, "listing.value", null);
            const newVal = changedFields.hasOwnProperty(f.field) ? changedFields[f.field].value : oldVal;
            normalizeImages(oldVal).forEach(url => imageUrls.add(url));
            normalizeImages(newVal).forEach(url => imageUrls.add(url));
          }
        });
      });

      const imageCache = {};
      const loadPromises = Array.from(imageUrls).map(async (url) => {
        const data = await loadImage(url);
        if (data.loaded) {
          imageCache[url] = data.element;
        }
      });
      await Promise.all(loadPromises);

      const lineHeight = 5;
      const arrowSymbol = "->";
      const pdfImgSize = 15; 
      const pdfImgSpacing = 17; 

      sortedGroups.forEach((group) => {
        const fields = groupedFields[group];
        if (!fields || fields.length === 0) return;

        checkPageBreak(15);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(16); 
        doc.setTextColor(40, 40, 40); 
        doc.text(`${group} :`, leftMargin, yPos);
        yPos += 14;

        fields.forEach((f) => {
          const label = f.label || f.field || "Unknown Field";
          const isImageField = f.field === "images" || (f.field && f.field.includes("image_locator")) || (f.ui && f.ui.input_type === "image");
          const isBulletField = (f.field && f.field.toLowerCase().includes('bullet')) || (label.toLowerCase().includes('bullet'));

          const oldVal = get(f, "listing.value", null);
          const isUpdated = changedFields.hasOwnProperty(f.field);
          const newVal = isUpdated ? changedFields[f.field].value : oldVal;

          checkPageBreak(12);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(11);
          doc.setTextColor(50, 50, 50);
          const labelLines = wrapText(doc, `${label}?`, contentWidth);
          doc.text(labelLines, leftMargin, yPos);
          yPos += (labelLines.length * lineHeight) + 2;

          if (isImageField) {
            if (isUpdated) {
               const oldImgs = normalizeImages(oldVal);
               if (oldImgs.length > 0) {
                 checkPageBreak(pdfImgSize + 10);
                 doc.setFont("helvetica", "normal");
                 doc.setFontSize(10);
                 doc.setTextColor(136, 136, 136);
                 doc.text("Old Images:", leftMargin + 8, yPos);
                 yPos += 6;
                 
                 let imgX = leftMargin + 8;
                 oldImgs.forEach(url => {
                   if (imgX + pdfImgSize > pageWidth - rightMargin) { imgX = leftMargin + 8; yPos += pdfImgSpacing; checkPageBreak(pdfImgSize + 5); }
                   if (imageCache[url]) doc.addImage(imageCache[url], 'JPEG', imgX, yPos, pdfImgSize, pdfImgSize, undefined, 'FAST');
                   imgX += pdfImgSpacing;
                 });
                 yPos += pdfImgSpacing + 2;
               }

               const newImgs = normalizeImages(newVal);
               const requiredBoxHeight = Math.ceil((newImgs.length * pdfImgSpacing) / (contentWidth - 25)) * pdfImgSpacing + 6;
               checkPageBreak(requiredBoxHeight + 10);
               
               doc.setFillColor(160, 250, 250); 
               doc.rect(leftMargin, yPos - 4, contentWidth, requiredBoxHeight > 21 ? requiredBoxHeight : 21, 'F');
               
               doc.setTextColor(34, 34, 34);
               doc.text(arrowSymbol, leftMargin + 3, yPos + 2);
               
               let imgX = leftMargin + 10;
               let currentImgY = yPos;
               newImgs.forEach(url => {
                 if (imgX + pdfImgSize > pageWidth - rightMargin) { imgX = leftMargin + 10; currentImgY += pdfImgSpacing; }
                 if (imageCache[url]) doc.addImage(imageCache[url], 'JPEG', imgX, currentImgY, pdfImgSize, pdfImgSize, undefined, 'FAST');
                 imgX += pdfImgSpacing;
               });
               yPos += (requiredBoxHeight > 21 ? requiredBoxHeight : 21) + 4;

            } else {
               const imgs = normalizeImages(oldVal);
               const requiredHeight = Math.ceil((imgs.length * pdfImgSpacing) / (contentWidth - 25)) * pdfImgSpacing;
               checkPageBreak(requiredHeight + 10);
               
               doc.setFont("helvetica", "normal");
               doc.setFontSize(10);
               doc.setTextColor(34, 34, 34);
               doc.text(arrowSymbol, leftMargin + 3, yPos + 2);
               
               if (imgs.length === 0) {
                 doc.text("-", leftMargin + 10, yPos + 2);
                 yPos += 8;
               } else {
                 let imgX = leftMargin + 10;
                 let currentImgY = yPos;
                 imgs.forEach(url => {
                   if (imgX + pdfImgSize > pageWidth - rightMargin) { imgX = leftMargin + 10; currentImgY += pdfImgSpacing; }
                   if (imageCache[url]) doc.addImage(imageCache[url], 'JPEG', imgX, currentImgY, pdfImgSize, pdfImgSize, undefined, 'FAST');
                   imgX += pdfImgSpacing;
                 });
                 yPos += requiredHeight + 6;
               }
            }

          } else {
            if (isUpdated) {
              doc.setFont("helvetica", "normal");
              doc.setFontSize(9);
              doc.setTextColor(136, 136, 136); 

              if (Array.isArray(oldVal) && isBulletField) {
                oldVal.forEach(item => {
                  const itemStr = `• ${getStringValue(item, isBulletField)}`;
                  const oldLines = wrapText(doc, itemStr, contentWidth - 10);
                  checkPageBreak(oldLines.length * lineHeight + 5);
                  doc.text(oldLines, leftMargin + 8, yPos);
                  yPos += (oldLines.length * lineHeight) + 2;
                });
              } else {
                const oldStr = getStringValue(oldVal, isBulletField);
                const oldLines = wrapText(doc, oldStr, contentWidth - 10);
                checkPageBreak(oldLines.length * lineHeight + 5);
                doc.text(oldLines, leftMargin + 8, yPos);
                yPos += (oldLines.length * lineHeight) + 2;
              }

              doc.setFontSize(10);
              doc.setTextColor(34, 34, 34);
              checkPageBreak(10);
              doc.text(arrowSymbol, leftMargin + 3, yPos + 1);

              if (Array.isArray(newVal) && isBulletField) {
                const oldArr = Array.isArray(oldVal) ? oldVal : [];
                newVal.forEach((item, idx) => {
                  const isItemChanged = JSON.stringify(item) !== JSON.stringify(oldArr[idx]);
                  const itemStr = `• ${getStringValue(item, isBulletField)}`;
                  const newLines = wrapText(doc, itemStr, contentWidth - 15);
                  const boxHeight = (newLines.length * lineHeight) + 4;
                  
                  checkPageBreak(boxHeight + 5);

                  if (isItemChanged) {
                    doc.setFillColor(160, 250, 250);
                    doc.rect(leftMargin + 8, yPos - 3, contentWidth - 8, boxHeight, 'F');
                  }
                  
                  doc.text(newLines, leftMargin + 10, yPos + 1);
                  yPos += boxHeight + 2;
                });

              } else {
                const newStr = getStringValue(newVal, isBulletField);
                const newLines = wrapText(doc, newStr, contentWidth - 15);
                const boxHeight = (newLines.length * lineHeight) + 6;
                checkPageBreak(boxHeight + 5);

                doc.setFillColor(160, 250, 250);
                doc.rect(leftMargin, yPos - 4, contentWidth, boxHeight, 'F');
                doc.text(newLines, leftMargin + 10, yPos + 1); 
                yPos += boxHeight + 4;
              }

            } else {
              doc.setFont("helvetica", "normal");
              doc.setFontSize(10);
              doc.setTextColor(50, 50, 50);
              
              doc.text(arrowSymbol, leftMargin + 3, yPos);

              if (Array.isArray(oldVal) && isBulletField) {
                oldVal.forEach(item => {
                  const itemStr = `• ${getStringValue(item, isBulletField)}`;
                  const textLines = wrapText(doc, itemStr, contentWidth - 15);
                  checkPageBreak(textLines.length * lineHeight + 5);
                  doc.text(textLines, leftMargin + 10, yPos);
                  yPos += (textLines.length * lineHeight) + 4;
                });
              } else {
                const oldStr = getStringValue(oldVal, isBulletField);
                const textLines = wrapText(doc, oldStr, contentWidth - 15);
                checkPageBreak(textLines.length * lineHeight + 5);
                doc.text(textLines, leftMargin + 10, yPos);
                yPos += (textLines.length * lineHeight) + 6;
              }
            }
          }
          
          yPos += 4; 
        });
        yPos += 8; 
      });

      const fileName = `${cleanStoreName}_${cleanAsin}_${cleanSku}_AmazonProductListing.pdf`;
      doc.save(fileName);
      
    } catch (error) {
      console.error("Failed to generate PDF", error);
    } finally {
      setIsDownloading(false);
    }
  };

  if (!groupedFields || Object.keys(groupedFields).length === 0) {
    return <div className="p-3 text-center">No preview data available.</div>;
  }

  const sortedGroups = Object.keys(groupedFields).sort((a, b) => {
    const getIndex = (name) => {
      const foundIndex = TAB_ORDER.findIndex(o => name.toLowerCase() === o.toLowerCase());
      return foundIndex !== -1 ? foundIndex : 999;
    };
    return getIndex(a) - getIndex(b);
  });

  return (
    <div className="preview-mode-wrapper p-4">
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px" }}>
        <button
          onClick={handleDownloadPdf}
          disabled={isDownloading}
          title="Download PDF"
          style={{
            background: "transparent",
            border: "none",
            padding: 0,
            cursor: isDownloading ? "wait" : "pointer",
            color: isDownloading ? "#ccc" : "#333",
            fontSize: "20px"
          }}
        >
          <FontAwesomeIcon icon={faDownload} />
        </button>
      </div>

      {sortedGroups.map((group) => {
        const fields = groupedFields[group];
        if (!fields || fields.length === 0) return null;

        return (
          <div key={group} className="mb-5">
            <h4 className="preview-group-title font-weight-bold" style={{ color: '#333333', fontSize: '22px', marginBottom: '15px' }}>
              {group} :
            </h4>
            
            <Row className="g-3">
              {fields.map((f, index) => {
                const label = f.label || f.field || "Unknown Field";
                const isImageField = f.field === "images" || (f.field && f.field.includes("image_locator")) || (f.ui && f.ui.input_type === "image");
                const isBulletField = (f.field && f.field.toLowerCase().includes('bullet')) || (label.toLowerCase().includes('bullet'));
                
                const oldVal = get(f, "listing.value", null);
                const isUpdated = changedFields.hasOwnProperty(f.field);
                const newVal = isUpdated ? changedFields[f.field].value : oldVal;

                return (
                  <Col md={12} key={index} className="mb-4">
                    <div className="preview-question-label" style={{ fontWeight: '600', color: '#555', marginBottom: '6px' }}>
                      {label}?
                    </div>
                    
                    <div>
                      {isUpdated ? (
                        <div className="d-flex flex-column">
                          <div className="preview-old-value" style={{ color: '#888', fontSize: '0.9em' }}>
                            {isImageField ? renderImagesList(oldVal) : renderValue(oldVal, isBulletField)}
                          </div>

                          <div className="preview-new-value-container mt-1 p-2 rounded d-flex" style={{ backgroundColor: (!isImageField && (!Array.isArray(newVal) || !isBulletField)) ? '#A0FAFA' : 'transparent' }}>
                            <span className="preview-answer-arrow mr-2" style={{ fontWeight: 'bold' }}>&#8594;</span>
                            <div className="preview-value-text" style={{ flex: 1 }}>
                              {isImageField ? renderImagesList(newVal) : (Array.isArray(newVal) ? renderUpdatedValueArray(oldVal, newVal, isBulletField) : renderValue(newVal, isBulletField))}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="preview-new-value-container d-flex">
                          <span className="preview-answer-arrow mr-2" style={{ color: '#888' }}>&#8594;</span>
                          <div className="preview-value-text" style={{ flex: 1 }}>
                            {isImageField ? renderImagesList(oldVal) : renderValue(oldVal, isBulletField)}
                          </div>
                        </div>
                      )}
                    </div>
                  </Col>
                );
              })}
            </Row>
          </div>
        );
      })}
    </div>
  );
}

export default AmazonProductListingPreview;